import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReportsApiService } from 'src/app/core/services/http/reports-api.service';
import { Note, PolicyDetail, PolicyTask, RequirementModalHeader, RequirementStatus } from 'src/app/shared/models/policy.models';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NgForm } from '@angular/forms';
import orderBy from 'lodash-es/orderBy';
import { CaseStatusNotesComponent } from 'src/app/portal/modals/case-status-notes/case-status-notes.component';
import { fadeIn } from 'src/app/animations';
import { RequirementStatusEnum } from 'src/app/shared/enums/policy-details.enum';
import { UpdatesHistoryComponent } from '../../modals/updates-history/updates-history.component';
import { TaskNoteV2 } from '../../models/policy-v2.models';
import { UpdateHistoryConfigModel } from '../../modals/updates-history/models/updates-history-config.model';

@Component({
  selector: 'app-requirements',
  templateUrl: './requirements.component.html',
  animations: [fadeIn],
  standalone: false
})
export class RequirementsComponent implements OnInit {
  @Input() requirementModalHeader: RequirementModalHeader | null = null;
  @Input() policyGuid!: string;
  @Input() isVariable!: boolean | null;
  @Input() policyStage!: string | null;
  @Input() policyStageDate!: Date | null;
  @Input() lobId!: number;
  @Input() hideResponseSubmission!: boolean | null;
  notes: TaskNoteV2[] = [];
  agentResponseForm: UntypedFormGroup = this.fb.group({});
  caseStatus!: PolicyTask;
  requirements!: PolicyTask[];
  filteredRequirements = this.requirements;
  showCaseStatusView = false;
  showRequirementView = false;
  showPolicyStageView = false;
  orderBy = orderBy;
  latestNote?: Note;
  loading = false;
  isUploading = false;
  isModalView!: boolean;
  policyDetails!: PolicyDetail;
  uploadingFiles: File[] = [];
  detailsUrl = '';
  showFeedback = false;
  feedbackMessage = '';
  isSuccess = false;
  requirementStatusList: RequirementStatus[] = [
    {
      Name: 'Open',
      ID: RequirementStatusEnum.Open
    },
    {
      Name: 'Complete',
      ID: RequirementStatusEnum.Complete
    }
  ];
  selectedRequirementStatus: RequirementStatus = this.requirementStatusList[0];


  get formMessage(): UntypedFormControl {
    return this.agentResponseForm.get('message') as UntypedFormControl;
  }
  get fileArray(): UntypedFormArray {
    return this.agentResponseForm.get('fileArray') as UntypedFormArray;
  }

  constructor(private reportsApiService: ReportsApiService, private _snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: any,
    private fb: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.configureVariables();
    if (this.lobId === 1) {
      this.getCaseStatusAndUploads();
    } else {
      this.getRequirementHistory();
    }
  }

  configureVariables(): void {
    this.policyGuid = this.policyGuid ?? this.data.policyGuid;
    this.isVariable = this.isVariable || (this.data ? this.data.isVariable : false);
    this.hideResponseSubmission = this.hideResponseSubmission ?? false;
    this.requirementModalHeader = this.data ? this.data.requirementHeader : null;
    this.isModalView = this.data ? true : false;
    this.lobId = this.lobId ?? this.data.lobId;
    if (this.isModalView) this.detailsUrl = this.data.detailsUrl;
    if (!this.policyStage && !this.data) {
      this.policyStage = null;
      this.policyStageDate = null;
    } else {
      this.policyStage = this.policyStage ?? this.data.policyStage;
      this.policyStageDate = this.policyStageDate ?? this.data.policyStageDate;
    }
  }

  getCaseStatusAndUploads(): void {
    let caseStatusHttp: Observable<PolicyTask>;
    if (this.isVariable) caseStatusHttp = this.reportsApiService.getVariablePolicyCaseStatusByGuid(this.policyGuid);
    else caseStatusHttp = this.reportsApiService.getPolicyCaseStatusByGuid(this.policyGuid);

    forkJoin({ caseStatus: caseStatusHttp, agentUploads: this.reportsApiService.getAgentUploadsByGuid(this.policyGuid) })
      .subscribe({
        next: (res) => {
          this.caseStatus = res.caseStatus;
          if (this.caseStatus) {
            this.showCaseStatusView = true;
            this.latestNote = res.caseStatus.Notes.filter(note => !note.IsExternalUser)[0];
            this.notes = this.getCombinedNotes(res.caseStatus.Notes, res.agentUploads.map(response => response.Notes).flat());
          } else {
            this.showPolicyStageView = true;
          }
          this.createForm();
        }
      }).add(() => {
        this.loading = false;
      });
  }


  getCombinedNotes(caseStatusNotes: Note[], uploadNotes: Note[]): TaskNoteV2[] {
    return caseStatusNotes.concat(uploadNotes)
      // remove duplicates
      .filter((value, index, self) => self.findIndex(s => s.NoteId === value.NoteId) === index)
      .sort((a, b) => new Date(b?.toString()).getTime() - new Date(a?.toString()).getTime())
      .map(note => {
        return { ...note, IsNew: false };
      });
  }

  filterRequirementByStatus(statusId: number): void {
    this.filteredRequirements = this.requirements.filter(x => x.TaskStatus?.ID === statusId);
  }

  getRequirementHistory(): void {
    this.reportsApiService.getPolicyRequirementHistoryByGuid(this.policyGuid).subscribe({
      next: requirements => {
        this.requirements = requirements.map(x => {
          return Object.assign(x, { FileArray: [] });
        });
        this.filterRequirementByStatus(this.selectedRequirementStatus.ID);
        if (this.requirements.length && this.requirements.length >= 1) this.showRequirementView = true;
        else this.showPolicyStageView = true;
        this.createForm();
      },
      error: () => {
        this.showPolicyStageView = true;
      }
    }).add(() => {
      this.loading = false;
    });
  }

  showCaseStatusNotes(notes: Note[]): void {
    this.dialog.open(CaseStatusNotesComponent, {
      data: {
        notes: notes
      },
      height: '74rem',
      width: '64rem'
    });
  }

  openUpdatesHistoryModal(): void {
    const dialog = this.dialog.open<UpdatesHistoryComponent, UpdateHistoryConfigModel, TaskNoteV2[]>(UpdatesHistoryComponent, {
      data: {
        PolicyGuid: this.policyGuid,
        MainTaskId: this.caseStatus.TaskId,
        ActionNeeded: this.caseStatus.ActionNeeded ?? false,
        MainTaskStatusId: this.caseStatus.TaskStatus?.ID,
        Title: this.caseStatus.TaskName ?? '',
        Notes: this.notes,
      }
    });

    dialog.afterClosed().subscribe({
      next: (newUpdates) => {
        if (newUpdates) this.ngOnInit();
      }
    });
  }

  createItem(data: any): UntypedFormGroup {
    return this.fb.group(data);
  }

  createForm(): void {
    this.agentResponseForm = this.fb.group({
      fileArray: this.fb.array([]),
      message: [''],
    });
  }

  onFileUpload(event: any, item?: PolicyTask): void {
    const files = event.target.files;

    if (files) {
      for (const file of files) {
        if (item) item?.FileArray.push(file);
        else this.fileArray.push(this.createItem({ file }));
      }

      // Clear input field so that files with same name can be uploaded at the same time
      (<HTMLInputElement>event.target).value = '';
    }
  }

  sendNotesOnlyToArc(taskId?: number, lifeLtcForm?: NgForm): void {
    this.isUploading = true;
    let toastMessage = ''; let toastClass = '';
    const formMessage = lifeLtcForm ? lifeLtcForm.value.Message.toString() : this.formMessage.value;

    this.reportsApiService.postPolicyNotes(formMessage, this.policyGuid, taskId).subscribe({
      next: () => {
        toastMessage = 'Note submitted successfully!';
        toastClass = 'is-success';
        this.isSuccess = true;
      },
      error: () => {
        toastMessage = 'Error: something went wrong';
        toastClass = 'is-error';
        this.isSuccess = false;
      },
      complete: () => {
        this.createForm();
        lifeLtcForm?.form.reset();
        this.showSystemFeedback(toastMessage);
      }
    }).add(() => {
      this.isUploading = false;
      this._snackBar.open(toastMessage, 'Close', { panelClass: [toastClass] });
      if (this.isSuccess) this.ngOnInit();
    });
  }

  sendFilesAndNotesToArc(taskId?: number, lifeLtcRequirement?: PolicyTask, lifeLtcForm?: NgForm): void {
    const formData = new FormData();
    this.isUploading = true;
    let formMessage = '';
    if (lifeLtcRequirement) {
      formMessage = lifeLtcRequirement.TaskName + ' - ';
      formMessage += lifeLtcRequirement.Message ? lifeLtcRequirement.Message.toString() : 'Agent Upload:';
      for (const file of lifeLtcRequirement.FileArray) {
        formData.append(formMessage, file, file.name);
      }
    } else {
      formMessage = this.formMessage.value ? this.formMessage.value : 'Agent Upload: ';
      for (const item of this.fileArray.value) {
        formData.append(formMessage, item.file, item.file.name);
      }
    }

    let toastMessage = ''; let toastClass = '';
    this.reportsApiService.postPolicyFiles(this.policyGuid, formData, lifeLtcRequirement ? lifeLtcRequirement.TaskId : taskId)

      .subscribe({
        next: res => {
          toastMessage = Object.keys(res).length === 1 ? '1 file uploaded successfully!' : res.length + ' files uploaded successfully!';
          toastClass = 'is-success';
          this.isSuccess = true;
          if (lifeLtcRequirement) {
            lifeLtcRequirement.FileArray = []; //Aware of this complaint will look at it later - Harry
          }
        },
        error: () => {
          toastMessage = 'Error: something went wrong';
          toastClass = 'is-error';
          this.isSuccess = false;
        },
        complete: () => {
          this.createForm();
          lifeLtcForm?.form.reset();
        }
      }).add(() => {
        this.showSystemFeedback(toastMessage);
        this._snackBar.open(toastMessage, 'Close', { panelClass: [toastClass] });
        this.isUploading = false;
        if (this.isSuccess) this.ngOnInit();
      });
  }

  resolveRequirement(taskId?: number): void {
    if (this.fileArray.value.length > 0)
      this.sendFilesAndNotesToArc(taskId);
    else if (this.formMessage.value)
      this.sendNotesOnlyToArc(taskId);
  }

  resolveLifeLtcRequirement(requirement: PolicyTask, form: NgForm): void {
    if (requirement.FileArray.length > 0) this.sendFilesAndNotesToArc(undefined, requirement, form);
    else this.sendNotesOnlyToArc(requirement.TaskId, form);
  }

  removeFile(index: number, lifeLtcRequirement?: PolicyTask): void {
    if (lifeLtcRequirement) lifeLtcRequirement.FileArray.splice(index, 1);
    else this.fileArray.removeAt(index);
  }

  showSystemFeedback(message: string): void {
    this.showFeedback = true;
    this.feedbackMessage = message;
    setTimeout(() => this.showFeedback = false, 4000);
  }
}
